import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import throttle from 'lodash/throttle'
import { Box, Flex } from '../../components/Box'
import MenuItems from '../../components/MenuItems'
import BottomNav from '../../components/BottomNav'
import Footer from '../../components/Footer'
import { useMatchBreakpoints } from '../../hooks'
import Logo from './components/Logo'
// import Panel from './components/Panel'
import UserBlock from './components/UserBlock'
import DmdPrice from './components/DmdPrice'
import { NavProps } from './types'
import { MENU_HEIGHT } from './config'
import MenuContext from './context'

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`

const StyledNav = styled.nav<{ showMenu: boolean; isBlackThemePage: boolean }>`
  position: fixed;
  top: ${({ showMenu }) => (showMenu ? 0 : `-${MENU_HEIGHT}px`)};
  left: 0;
  transition: top 0.2s;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  width: 100%;
  height: ${MENU_HEIGHT}px;
  background: ${({ isBlackThemePage }) => (isBlackThemePage ? '#161616' : '#00334a')};
  z-index: 20;
  transform: translate3d(0, 0, 0);
`

const BodyWrapper = styled(Box)`
  position: relative;
  display: flex;
`

const Inner = styled.div<{ showMenu: boolean; isBlackThemePage: boolean }>`
  flex-grow: 1;
  margin-top: ${({ showMenu }) => (showMenu ? `${MENU_HEIGHT}px` : 0)};
  transition: margin-top 0.2s, margin-left 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translate3d(0, 0, 0);
  max-width: 100%;
  background-color: ${({ theme, isBlackThemePage }) =>
    isBlackThemePage ? 'linear-gradient(180deg, #101010 0%, #101010 100%)' : theme.colors.background3};
  height: calc(100vh - ${MENU_HEIGHT}px);
  overflow: auto;

  ${({ theme }) => theme.mediaQueries['2xl']} {
    background-image: url('/images/background.svg');
    background-repeat: no-repeat;
    background-position: bottom;
    background-size: 100% auto;
  }
`

const Menu: React.FC<NavProps> = ({
  linkComponent = 'a',
  account,
  login,
  logout,
  dmdPriceUsd,
  links,
  footerLinks,
  // profile,
  children,
}) => {
  const { pathname } = useLocation()
  const { is2xl, isXl } = useMatchBreakpoints()
  const isMobile = is2xl === false && isXl === false
  const [showMenu, setShowMenu] = useState(true)
  const refPrevOffset = useRef(window.pageYOffset)

  useEffect(() => {
    const handleScroll = () => {
      const currentOffset = window.pageYOffset
      const isBottomOfPage = window.document.body.clientHeight === currentOffset + window.innerHeight
      const isTopOfPage = currentOffset === 0
      // Always show the menu when user reach the top
      if (isTopOfPage) {
        setShowMenu(true)
      }
      // Avoid triggering anything at the bottom because of layout shift
      else if (!isBottomOfPage) {
        if (currentOffset < refPrevOffset.current || currentOffset <= MENU_HEIGHT) {
          // Has scroll up
          setShowMenu(true)
        } else {
          // Has scroll down
          setShowMenu(false)
        }
      }
      refPrevOffset.current = currentOffset
    }
    const throttledHandleScroll = throttle(handleScroll, 200)

    window.addEventListener('scroll', throttledHandleScroll)
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll)
    }
  }, [])
  useEffect(() => {
    document.getElementById('inner').scrollTo(0, 0)
  }, [pathname])

  // Find the home link if provided
  const homeLink = links.find((link) => link.label === 'Home')

  const isBlackThemePage = useMemo(() => pathname === '/' || pathname === '/nft/launchpad', [pathname])

  return (
    <MenuContext.Provider value={{ linkComponent }}>
      <Wrapper>
        <StyledNav showMenu={showMenu} isBlackThemePage={isBlackThemePage}>
          <Flex>
            <Logo href={homeLink?.href ?? '/'} />
            {!isMobile && <MenuItems items={links} ml="40px" />}
          </Flex>
          <Flex alignItems="center" height="100%">
            <DmdPrice dmdPriceUsd={dmdPriceUsd} />
            {!!login && !!logout && (
              <Flex>
                <UserBlock account={account} login={login} logout={logout} />
              </Flex>
            )}
          </Flex>
        </StyledNav>
        <BodyWrapper>
          {/* <Panel
          isPushed={isPushed}
          isMobile={isMobile}
          showMenu={showMenu}
          isDark={isDark}
          toggleTheme={toggleTheme}
          langs={langs}
          setLang={setLang}
          currentLang={currentLang}
          // dmdPriceUsd={dmdPriceUsd}
          pushNav={setIsPushed}
          links={links}
        /> */}
          <Inner showMenu={showMenu} isBlackThemePage={isBlackThemePage} id="inner">
            {children}
            <Footer
              items={footerLinks}
              isBlackThemePage={isBlackThemePage}
              dmdPriceUsd={dmdPriceUsd}
              mb={isMobile ? `${36}px` : '0'}
            />
          </Inner>
        </BodyWrapper>
        {isMobile && <BottomNav items={links} />}
      </Wrapper>
    </MenuContext.Provider>
  )
}

export default Menu
